import React, { Fragment } from 'react';
import { Fund } from 'src/components/Exchange/types';
import config from '../../../../config/config';
import {
  COLLEGE_DIVIDEND_PHONENUMBER,
  COLLEGE_FUND_NOTE_PHONE_NUMBERS,
  FundNoteExchange,
  FundNoteExchangeCollege,
  TOLL_FREE_NUMBER,
} from '../../../../settings/settings';
import { coalesce } from '../../../../utils/utils';
import If from '../../../Shared/If/If';
import Phone from '../../../Shared/Phone/Phone';
import AddressUndeliverableFundNoteTip from './AddressUndeliverableFundNoteTip';
import FundNote from './FundNote';
import './FundNote.module.scss';
import PendingTransactionFundNoteTip from './PendingTransactionFundNoteTip';
import RestrictedCountryFundNoteTip from './RestrictedCountryFundNoteTip';

const CollegeExchangeFundNotes: React.FC<{ fund: Fund }> = ({ fund }) => {
  const phoneNumber = coalesce(
    COLLEGE_FUND_NOTE_PHONE_NUMBERS[fund.productType],
    TOLL_FREE_NUMBER
  );
  return (
    <Fragment>
      <If test={fund.notes[FundNoteExchange.NOT_ELIGIBLE]}>
        <FundNote
          labelSubText={''}
          labelText="Ineligible Exchange Account"
          tipHeader="Ineligible Exchange Account"
          tipMessage={
            <div className="exclude">
              Exchanges among your selected investment(s) and this investment
              are ineligible due to differences in the account details.{' '}
              <a
                className="exclude no-break"
                href={`${config.url}/personal-investing/help/transactions.html`}
              >
                Learn why.
              </a>
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteExchangeCollege.ADDRESS_UNDELIVERABLE]}>
        <FundNote
          labelSubText=""
          labelText="Address Undeliverable"
          tipHeader="Address Undeliverable"
          tipMessage={
            <AddressUndeliverableFundNoteTip phoneNumber={phoneNumber} />
          }
        />
      </If>
      <If test={fund.notes[FundNoteExchangeCollege.FROM_ZERO]}>
        <FundNote
          labelSubText=""
          labelText="Exchange Unavailable"
          tipHeader="Exchange Unavailable"
          tipMessage="You are unable to exchange out of this account due to zero units. An exchange into this account is still allowed, as long as the portfolio is still open and accepting contributions."
        />
      </If>
      <If test={fund.notes[FundNoteExchangeCollege.COUNTRY_RESTRICTED]}>
        <FundNote
          labelSubText="Requires Permanent US Address"
          labelText="Restricted"
          tipHeader="Restricted Country"
          tipMessage={<RestrictedCountryFundNoteTip />}
        />
      </If>
      <If
        test={
          fund.notes[FundNoteExchangeCollege.AUTOMATED_SYSTEMS_STOP_EXCHANGE]
        }
      >
        <FundNote
          labelSubText=""
          labelText="Exchange Unavailable"
          tipHeader="Exchange Unavailable"
          tipMessage={
            <div className="exclude">
              Your account has a feature on it that restricts exchanges. Call{' '}
              <Phone phoneNumber={phoneNumber} /> to discuss options.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteExchangeCollege.ALASKA_ACT_RESTRICTION]}>
        <FundNote
          labelSubText=""
          labelText="Exchange Unavailable"
          tipHeader="Exchange Unavailable"
          tipMessage={
            <div className="exclude">
              Exchanges from the {fund.fundName} are unavailable online; please
              call <Phone phoneNumber={phoneNumber} />
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteExchangeCollege.TWO_EXCHANGES_PER_YEAR]}>
        <FundNote
          labelSubText=""
          labelText="Exchange Unavailable"
          tipHeader="Exchange Unavailable"
          tipMessage="You are only permitted to change investment allocations per beneficiary twice per calendar year. Our records indicate you have already changed investment allocations for this beneficiary twice this year."
        />
      </If>
      <If test={fund.notes[FundNoteExchangeCollege.PENDING]}>
        <FundNote
          labelSubText=""
          labelText="Transaction Pending"
          tipHeader="Transaction Pending"
          tipMessage={<PendingTransactionFundNoteTip />}
        />
      </If>
      <If test={fund.notes[FundNoteExchangeCollege.PENDING_TRANSACTION]}>
        <FundNote
          labelSubText=""
          labelText="Transaction Pending"
          tipHeader="Transaction Pending"
          tipMessage={<PendingTransactionFundNoteTip allFunds={true} />}
        />
      </If>
      <If test={fund.notes[FundNoteExchangeCollege.RETURN_MAIL_VERIFY_ADDRESS]}>
        <FundNote
          labelSubText=""
          labelText="Exchange Unavailable"
          tipHeader="Exchange Unavailable"
          tipMessage="We have received undeliverable mail from the Post Office for your address on record. Please verify your address on record is correct and update if necessary."
        />
      </If>
      <If test={fund.notes[FundNoteExchangeCollege.STOP_TRANSFER]}>
        <FundNote
          labelSubText=""
          labelText="Stop Transfer"
          tipHeader="Stop Transfer"
          tipMessage={
            <div className="exclude">
              Your account has a feature on it that restricts exchanges. Call{' '}
              <Phone phoneNumber={phoneNumber} /> to discuss options.
            </div>
          }
        />
      </If>
      <If
        test={
          fund.notes[
            FundNoteExchange.WITHIN_CURRENT_INVESTMENTS ||
              FundNoteExchange.ACH_DIVIDEND
          ]
        }
      >
        <FundNote
          labelSubText=""
          labelText="Exchange Within Current Investments"
          tipHeader="Exchange Within Current Investments"
          tipMessage={
            <div className="exclude">
              This investment can only be exchanged within existing investments
              in your portfolio because of its dividend settings. Call{' '}
              <Phone phoneNumber={COLLEGE_DIVIDEND_PHONENUMBER} /> to update
              your settings.
            </div>
          }
        />
      </If>
      <If test={fund.notes[FundNoteExchangeCollege.UNAVAILABLE]}>
        <FundNote
          labelSubText=""
          labelText="Exchange Restricted"
          tipHeader="Exchange Restricted"
          tipMessage={
            <div className="exclude">
              ​​​​​​​This portfolio is not set up to exchange online. Please
              call an associate at{' '}
              <Phone phoneNumber={COLLEGE_DIVIDEND_PHONENUMBER} /> to update the
              appropriate settings.
            </div>
          }
        />
      </If>
      <If
        test={
          fund.notes[FundNoteExchangeCollege.UNAVAILABLE_FOR_REDEMPTION_ONLINE]
        }
      >
        <FundNote
          labelSubText=""
          labelText="Unavailable Online"
          tipHeader="Unavailable Online"
          tipMessage={
            <div className="exclude">
              Online/phone exchange disabled. Call{' '}
              <Phone phoneNumber={phoneNumber} /> to discuss options.
            </div>
          }
        />
      </If>
      <If
        test={
          fund.notes[FundNoteExchangeCollege.AUTOMATED_SYSTEMS_STOP_ALL] ||
          fund.notes[FundNoteExchangeCollege.AUTOMATED_SYSTEMS_STOP_CHECK] ||
          fund.notes[FundNoteExchangeCollege.AUTOMATED_SYSTEMS_STOP_PURCHASE] ||
          fund.notes[FundNoteExchangeCollege.TO_AUTOMATED_SYSTEMS_STOP_EXCHANGE]
        }
      >
        <FundNote
          labelSubText=""
          labelText="Exchange Unavailable"
          tipHeader="Exchange Unavailable"
          tipMessage={
            <p className="exclude">
              Your account has a feature on it that restricts exchanges. Call{' '}
              <Phone phoneNumber={phoneNumber} /> to discuss options.
            </p>
          }
        />
      </If>
      <If test={fund.notes[FundNoteExchangeCollege.EXCHANGE_LIMIT_REACHED]}>
        <FundNote
          labelSubText=""
          labelText="Exchange Unavailable"
          tipHeader="Exchange Unavailable"
          tipMessage={
            <div className="exclude">
              You are only permitted to change investment allocations per
              beneficiary twice per calendar year. Our records indicate you have
              already changed investment allocations for this beneficiary twice
              this year.
            </div>
          }
        />
      </If>
    </Fragment>
  );
};

export default CollegeExchangeFundNotes;
